import {
  Button,
  Col,
  Row,
  Tooltip,
  Modal,
  Input,
  Select,
  Checkbox,
  message,
  Tag,
} from "antd";
import React, { useState, useEffect, useRef } from "react";
import { showNotice } from "../helper/notify";
import "../styles/db.css";
import "../styles/libraryTable.css";
import { PlusOutlined, FileTextOutlined } from "@ant-design/icons";
import PageHeader from "../components/generic/PageHeader";
import TableComponent from "../components/table/TableComponent";
import DownloadFile from "../assets/Sample Functional Spec.xlsx";
import { file_spec_columns } from "../constants/Table Columns";
import ColumnToggle from "../components/generic/ColumnToggle";
import { useColumnToggle } from "../helper/useColumnToggle";
import { request, postFormData } from "../helper/axios_helper";
import { uploadFSD, writeToDB } from "../service/fsd.service";
import {
  documentTypeList,
  specFileType,
  sqlFileType,
} from "../constants/List Constants";
import { getFileExtension } from "../helper/string_helper";
import { getAllConnection, testSql } from "../service/db.service";
import SpecSuitesModal from "../components/modal/SpecSuitesModal";
import SpecReviewModal from "../components/filespec/SpecReviewModal";
import SpecViewerModal from "../components/filespec/SpecViewerModal";
import EditSpecModal from "../components/filespec/EditSpecModal";
import SpecVersionHistoryModal from "../components/filespec/SpecVersionHistoryModal";
import { parseSpecFile } from "../helper/spec_parser";

const { TextArea } = Input;
const { Option } = Select;

const OPTIONAL_FILE_SPEC_COLUMNS = [
  "category",
  "user",
  "createDate",
  "comments",
];

function FileSpec() {
  const [showPopup, setShowPopup] = useState(false);
  const [fileExtension, setFileExtension] = useState("");
  const [fileType, setFileType] = useState("");
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [file, setFile] = useState("");
  const [specName, setSpecName] = useState("");
  const [comments, setComments] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [createSuite, setCreateSuite] = useState(false);
  const [tableData, setTableData] = useState([]);
  const existingNames = useRef(new Set());
  const [documentTypeError, setDocumentTypeError] = useState("info");
  const [fileTypeError, setFileTypeError] = useState("info");
  const [specInvalid, setSpecInvalid] = useState("info");
  const [fileNameError, setFileNameError] = useState("black");
  const [dbConnectionsList, setDbConnectionsList] = useState([]);
  const [testConnection, setTestConnection] = useState("");
  const [fileContent, setFileContent] = useState("");
  const [environmentType, setEnvironmentType] = useState("");
  const [documentFilter, setDocumentFilter] = useState(["spec", "sql"]);
  const [uploading, setUploading] = useState(false);
  const [suitesModalOpen, setSuitesModalOpen] = useState(false);
  const [selectedSpec, setSelectedSpec] = useState(null);
  // Review-before-upload + post-upload viewer
  const [reviewOpen, setReviewOpen] = useState(false);
  const [parsedSpec, setParsedSpec] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerRecord, setViewerRecord] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsRecord, setVersionsRecord] = useState(null);

  const handleEdit = (record) => {
    setEditRecord(record);
    setEditOpen(true);
  };
  const handleVersions = (record) => {
    setVersionsRecord(record);
    setVersionsOpen(true);
  };

  const options = [{ value: "not implemented" }];

  const fetchData = () => {
    request("GET", "/api/auth/filespec/fetchall").then((res) => {
      setTableData(res.data);
      setTableLoading(false);
      // Build local name set — used for instant duplicate check without API calls
      existingNames.current = new Set(
        (res.data || []).map((s) =>
          (s.specName || s.displayName || "").toLowerCase(),
        ),
      );
    });
  };
  const handleSuiteClick = (record) => {
    setSelectedSpec(record.specName);
    setSuitesModalOpen(true);
  };

  const getDbConnections = () => {
    getAllConnection().then((res) => {
      setDbConnectionsList(res.data);
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onFileTypeChange = (value) => {
    if (value.length > 0) setFileTypeError("info");
    setFileType(value);
  };

  const onEnvironmentChange = (value) => setEnvironmentType(value);

  const onDocChange = (value) => {
    setDocumentType(value);
    setFileType(null);
    if (value === "spec") {
      setFileTypeOptions(specFileType);
    } else {
      setFileTypeOptions(sqlFileType);
      getDbConnections();
    }
  };

  const onClickNewDocument = () => setShowPopup(true);

  const tagRender = (props) => {
    const { label, closable, onClose } = props;
    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color="green"
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {label}
      </Tag>
    );
  };
  const onClickSave = () => {
    if (!documentType || documentType.length < 1) setDocumentTypeError("error");
    if (!fileType || fileType.length < 1) setFileTypeError("error");
    if (!specName || specName.length < 1) setSpecInvalid("error");
    if (!file || file.length < 1) setFileNameError("red");

    if (!documentType || !specName || comments.length > 255 || !file) {
      message.error("ERROR: Please ensure all mandatory fields are populated");
      return;
    }

    if (fileType !== fileExtension) {
      message.error("File type does not match with uploaded file!");
      return;
    }

    if (documentType === "spec") {
      validateAndUpload();
    } else if (documentType === "sql") {
      if (!testConnection || testConnection.length < 1) {
        message.error("Select database connection");
        return;
      }
      testSqlQuery(testConnection, fileContent);
    }
  };

  // ─── Step 1: Validate the spec file ──────────────────────────
  const validateAndUpload = async () => {
    setUploading(true);
    try {
      // ─── Parse once, client-side (sheet check + review data) ───
      const parsed = await parseSpecFile(file);
      if (parsed.missingSheets.length > 0) {
        message.error(
          `File is missing required sheet(s): ${parsed.missingSheets.join(", ")}`,
          8,
        );
        return;
      }
      const formData = new FormData();
      formData.append("fileUpload", file);

      const validateRes = await postFormData(
        "/api/auth/filespec/validate",
        formData,
      );

      // Guard: if a proxy/CDN (e.g. CloudFront SPA fallback) returned HTML
      // instead of the API's JSON, surface a clear gateway error rather than
      // the misleading "validation failed" message.
      const validateBody = validateRes.data;
      if (typeof validateBody !== "object" || validateBody === null) {
        message.error(
          "Upload could not reach the server — the gateway returned an unexpected response. Please try again or contact support if this persists.",
          8,
        );
        return;
      }

      if (!validateBody.successful) {
        const checks = validateBody.data?.[0] || {};
        const failed = Object.keys(checks).filter((k) => checks[k] === false);
        message.error(
          failed.length
            ? `File validation failed: ${failed.join("; ")}`
            : "File validation failed. Please check the file format.",
          8,
        );
        return;
      }

      // Validation passed — show the review modal before uploading
      setParsedSpec(parsed);
      setReviewOpen(true);
    } catch (err) {
      message.error("Validation failed: " + (err.message || "Unknown error"));
    } finally {
      setUploading(false);
    }
  };

  // ─── Confirm from the review modal → perform the actual upload ──
  const handleConfirmUpload = async () => {
    setConfirming(true);
    try {
      await uploadToS3(); // uploads to S3 → saveMetadata → closes popup + refresh
      setReviewOpen(false);
      setParsedSpec(null);
    } finally {
      setConfirming(false);
    }
  };

  const handleView = (record) => {
    setViewerRecord(record);
    setViewerOpen(true);
  };

  // ─── Step 2: Upload file to S3 ────────────────────────────────
  const uploadToS3 = async () => {
    try {
      const formData = new FormData();
      formData.append("fileUpload", file);

      const s3Res = await postFormData("/api/auth/storage/upload", formData);

      if (!s3Res.data.successful) {
        message.error("Document upload failed.");
        return;
      }

      const s3Path = s3Res.data.message;

      // S3 upload succeeded — save metadata to DB
      await saveMetadata(s3Path);
    } catch (err) {
      message.error("S3 upload failed: " + (err.message || "Unknown error"));
    }
  };

  // ─── Step 3: Save file spec metadata to DB ───────────────────
  const saveMetadata = async (s3Path) => {
    try {
      await uploadFSD(
        specName,
        comments,
        s3Path,
        documentType,
        "not implemented",
        fileType,
      );

      // Metadata saved — now write test suite data if applicable
      await writeToDB(
        specName,
        createSuite,
        documentType,
        file,
        testConnection || null,
        fileContent || null,
      );

      setShowPopup(false);
      fetchData();
      showNotice(
        "success",
        "Document upload successful",
        `File uploaded to ${s3Path}`,
      );
    } catch (err) {
      // Prefer the backend's reason (e.g. "a document named X already exists in
      // another project") over axios's generic "Request failed with status code 400".
      const serverMessage = err.response?.data?.message;
      message.error(
        serverMessage ||
          "Failed to save document: " + (err.message || "Unknown error"),
        8,
      );
    }
  };

  const onDisplayNameChange = (e) => {
    const value = e.target.value;
    setSpecName(value);
    if (!value.trim()) {
      setSpecInvalid("info");
      return;
    }
    const isDuplicate = existingNames.current.has(value.trim().toLowerCase());
    setSpecInvalid(isDuplicate ? "error" : "info");
  };

  const getContent = (fileObject) => {
    if (fileObject) {
      const reader = new FileReader();
      reader.onload = (e) => setFileContent(e.target.result);
      reader.onerror = (e) =>
        console.error("Error reading file:", e.target.error);
      reader.readAsText(fileObject);
    }
  };

  const testSqlQuery = async (displayName, query) => {
    setUploading(true);
    try {
      const res = await testSql(displayName, query);
      if (res.data.message === "Fail" || res.data.message !== "Success") {
        message.warning(
          "The SQL does not return any response. Proceeding with upload.",
        );
        return;
      }
      await uploadToS3();
    } catch (err) {
      message.error("SQL test failed: " + (err.message || "Unknown error"));
    } finally {
      setUploading(false);
    }
  };

  const uniqueEnvironment = [
    ...new Set(dbConnectionsList.map((data) => data.environmentName)),
  ];

  const onChange = (pagination, filters, sorter) => {
    filters.documentType !== null
      ? setDocumentFilter(filters.documentType)
      : setDocumentFilter(["spec", "sql"]);
  };

  const fileTypeFilters = documentFilter.map((filter) => {
    if (filter === "spec") return [{ text: "xlsx", value: "xlsx" }];
    if (filter === "sql")
      return [
        { text: "txt", value: "txt" },
        { text: "sql", value: "sql" },
      ];
    return [];
  });

  const updatedFilter = file_spec_columns(
    handleSuiteClick,
    handleView,
    handleEdit,
    handleVersions,
  ).map((col) => {
    if (col.key === "type") {
      return {
        ...col,
        filters: fileTypeFilters.flat(),
        onFilter: (value, record) => record.documentFileType === value,
      };
    }
    return col;
  });

  const { visibleColumns, columnToggleProps } = useColumnToggle(
    updatedFilter,
    OPTIONAL_FILE_SPEC_COLUMNS,
    "arc_cols_filespec",
  );

  return (
    <div className="db-container">
      <PageHeader
        icon={<FileTextOutlined />}
        title="Document Library"
        tooltip="Upload new or maintain existing file specification documents"
        actions={
          <>
            <a download="template" href={DownloadFile}>
              <Button className="db-add">Download template</Button>
            </a>
            <Button
              className="db-add"
              icon={<PlusOutlined />}
              onClick={onClickNewDocument}
            >
              New Document
            </Button>
            <ColumnToggle {...columnToggleProps} />
          </>
        }
      />
      <Modal
          centered
          open={showPopup}
          onOk={onClickSave}
          onCancel={() => setShowPopup(false)}
          destroyOnClose
          footer={[
            <Button
              key="back"
              onClick={() => setShowPopup(false)}
              disabled={uploading}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={onClickSave}
              loading={uploading}
            >
              Validate & Save
            </Button>,
          ]}
          width="700px"
          maskClosable={false}
        >
          <Row style={{ paddingBottom: "20px" }}>
            <p className="modal-header-text">Upload Document Library</p>
          </Row>
          <Row>
            <Col span={6}>
              <p className="spec-modal-label">Select Label (s) :</p>
            </Col>
            <Col span={18}>
              <p>
                <Select
                  mode="multiple"
                  tagRender={tagRender}
                  style={{ width: "100%" }}
                  options={options}
                  placeholder="select label(s)"
                />
              </p>
            </Col>
          </Row>
          <Row>
            <Col span={6}>
              <p className="spec-modal-label">Document Type * :</p>
            </Col>
            <Col span={18}>
              <p>
                <Select
                  style={{ width: "100%" }}
                  options={documentTypeList}
                  placeholder="document type"
                  onChange={onDocChange}
                  status={documentTypeError}
                />
              </p>
            </Col>
          </Row>
          <Row>
            <Col span={6}>
              <p className="spec-modal-label">Display Name * :</p>
            </Col>
            <Col span={18}>
              <p>
                <Input
                  status={specInvalid}
                  placeholder="Display Name"
                  maxLength={255}
                  showCount
                  onChange={onDisplayNameChange}
                />
              </p>
            </Col>
          </Row>
          <Row>
            <Col span={6}>
              <p className="spec-modal-label">File Type * :</p>
            </Col>
            <Col span={18}>
              <p>
                <Select
                  value={fileType}
                  style={{ width: "100%" }}
                  options={fileTypeOptions}
                  placeholder="file type"
                  onChange={onFileTypeChange}
                  status={fileTypeError}
                />
              </p>
            </Col>
          </Row>
          <Row>
            <Col span={6}>
              <p className="spec-modal-label">Upload Document * :</p>
            </Col>
            <Col span={18}>
              <p>
                <input
                  type="file"
                  style={{ color: fileNameError }}
                  onChange={(e) => {
                    const f = e.target.files[0];
                    const extension = getFileExtension(f.name);
                    setFileExtension(extension);
                    setFile(f);
                    setFileNameError("black");
                    extension === "sql" || extension === "txt"
                      ? getContent(f)
                      : setFileContent("");
                  }}
                />
              </p>
            </Col>
          </Row>
          {documentType === "sql" && (
            <div>
              <Row>
                <Col span={6}>
                  <p className="spec-modal-label">Select environment * :</p>
                </Col>
                <Col span={18}>
                  <p>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="select environment"
                      onChange={onEnvironmentChange}
                    >
                      {uniqueEnvironment.map((data) => (
                        <Option key={data} value={data}>
                          {data}
                        </Option>
                      ))}
                    </Select>
                  </p>
                </Col>
              </Row>
              <Row>
                <Col span={6}>
                  <p className="spec-modal-label">Select Database * :</p>
                </Col>
                <Col span={18}>
                  <p>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="select database connections"
                      onChange={(e) => setTestConnection(e)}
                    >
                      {dbConnectionsList
                        .filter(
                          (data) => data.environmentName === environmentType,
                        )
                        .map((data) => (
                          <Option
                            key={data.displayName}
                            value={data.displayName}
                          >
                            {data.displayName}
                          </Option>
                        ))}
                    </Select>
                  </p>
                </Col>
              </Row>
            </div>
          )}
          <Row>
            <Col span={6}>
              <p className="spec-modal-label">Comments :</p>
            </Col>
            <Col span={18}>
              <p>
                <TextArea
                  placeholder="comment/description"
                  value={comments}
                  maxLength={255}
                  showCount
                  onChange={(e) => setComments(e.target.value)}
                  autoSize={{ minRows: 3, maxRows: 5 }}
                />
              </p>
            </Col>
          </Row>
          <p>
            <Checkbox
              checked={createSuite}
              onChange={() => setCreateSuite(!createSuite)}
            >
              Generate test suite
            </Checkbox>
          </p>
        </Modal>
      <Row className="table-container">
        <TableComponent
          wrapClassName="arc-library-table"
          columns={visibleColumns}
          data={tableData}
          pageSize={20}
          loading={tableLoading}
          onChange={onChange}
        />
      </Row>
      <SpecSuitesModal
        isOpen={suitesModalOpen}
        onClose={() => setSuitesModalOpen(false)}
        specName={selectedSpec}
      />
      <SpecReviewModal
        open={reviewOpen}
        meta={{ specName, fileName: file?.name }}
        parsed={parsedSpec}
        confirming={confirming}
        onConfirm={handleConfirmUpload}
        onCancel={() => setReviewOpen(false)}
      />
      <SpecViewerModal
        open={viewerOpen}
        specRecord={viewerRecord}
        onClose={() => setViewerOpen(false)}
      />
      <EditSpecModal
        open={editOpen}
        record={editRecord}
        onClose={() => setEditOpen(false)}
        onSaved={fetchData}
      />
      <SpecVersionHistoryModal
        open={versionsOpen}
        record={versionsRecord}
        onClose={() => setVersionsOpen(false)}
        onRestored={fetchData}
      />
    </div>
  );
}

export default FileSpec;
